import axios from 'axios';
import { getApiBaseUrl } from '../config';

const api = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  config.baseURL = `${getApiBaseUrl()}/api`;
  return config;
});

// Auth
export const login = (loginOrEmail, password) => {
  const s = typeof loginOrEmail === 'string' ? loginOrEmail.trim() : '';
  const body = s.includes('@') ? { email: s, password } : { login: s, password };
  return api.post('/auth/login', body);
};
export const logout = () => api.post('/auth/logout');
export const getMe = () => api.get('/auth/me');

// Dashboard
export const getDashboardMetrics = () => api.get('/dashboard/metrics');
export const getTopOpportunities = (limit = 10) => api.get(`/dashboard/top-opportunities?limit=${limit}`);
export const getByTherapeuticArea = () => api.get('/dashboard/by-therapeutic-area');

// Drugs
export const getDrugs = (params = {}) => api.get('/drugs', { params });
export const getFeaturedDrugs = () => api.get('/drugs/featured');
export const getDrug = (drugId) => api.get(`/drugs/${drugId}`);
export const getDrugComparison = (drugId) => api.get(`/drugs/${drugId}/comparison`);

// Evidence
export const getEvidence = (params = {}) => api.get('/evidence', { params });

// Geography
export const getRegions = () => api.get('/geography/regions');
export const getRegionData = (regionCode) => api.get(`/geography/${regionCode}`);

// Simulator
export const calculateSavings = (adoptionRate) => api.get(`/simulator/calculate?adoption_rate=${adoptionRate}`);

// Pipeline
export const getPipelineOverview = () => api.get('/pipeline/overview');
export const getPipelineStage = (stage) => api.get(`/pipeline/stage/${stage}`);
export const movePipeline = (genericId, newStage) => api.post(`/pipeline/move?generic_id=${genericId}&new_stage=${newStage}`);
export const assignPipelineItem = (genericId, assignedTo, priority) => api.post('/pipeline/assign', { generic_id: genericId, assigned_to: assignedTo, priority });

// Decisions (Action Center)
export const createDecision = (data) => api.post('/decisions', data);
export const getDecisions = (params = {}) => api.get('/decisions', { params });
export const updateDecision = (decisionId, status, notes) => api.put(`/decisions/${decisionId}?status=${status}${notes ? `&notes=${notes}` : ''}`);
export const getDecisionTimeline = (genericId) => api.get(`/decisions/timeline/${genericId}`);

// Executions
export const createExecution = (data) => api.post('/executions', data);
export const getExecutions = (limit = 50) => api.get(`/executions?limit=${limit}`);

// Savings Ledger
export const recordSavings = (data) => api.post('/savings', data);
export const getSavingsLedger = (params = {}) => api.get('/savings/ledger', { params });
export const getSavingsSummary = () => api.get('/savings/summary');
export const getSavingsEngine = () => api.get('/savings/engine');
export const exportSavingsCSV = () => api.get('/savings/export', { responseType: 'blob' });

// Interventions
export const getInterventions = (status = 'active') => api.get(`/interventions?status=${status}`);
export const dismissIntervention = (id) => api.put(`/interventions/${id}/dismiss`);
export const actOnIntervention = (id) => api.put(`/interventions/${id}/act`);

// Formulary
export const getFormulary = (status) => api.get('/formulary', { params: { status } });
export const updateFormularyEntry = (drugId, status, notes) => api.put(`/formulary/${drugId}?status=${status}${notes ? `&notes=${notes}` : ''}`);

// Committee Packs
export const generateCommitteePack = (drugId, genericIds) => api.post('/committee-pack/generate', { drug_id: drugId, generic_ids: genericIds });
export const getCommitteePacks = (limit = 20) => api.get(`/committee-packs?limit=${limit}`);
export const downloadCommitteePackPdf = (packId) => api.get(`/committee-pack/${packId}/pdf`, { responseType: 'blob' });
export const generateCommitteePackPdf = (drugId, genericIds) => api.post('/committee-pack/generate-pdf', { drug_id: drugId, generic_ids: genericIds }, { responseType: 'blob' });

// CFO Dashboard
export const getCFODashboard = () => api.get('/cfo-dashboard');

// Pharmacy Dashboard
export const getPharmacyDashboard = () => api.get('/pharmacy-dashboard');

// AI
export const generateAIBrief = (drugId, mode) => api.post(`/ai/brief?drug_id=${drugId}&mode=${mode}`);
export const getStrategicAdvice = () => api.post('/ai/strategic-advice');
export const getAnalystBrief = (uploadId, mode) => api.post('/ai/analyst-brief', { upload_id: uploadId, mode });

// Waste Detection
export const getWasteAlerts = (limit = 5) => api.get(`/waste-alerts?limit=${limit}`);

// Reports
export const getReports = (limit = 20) => api.get(`/reports?limit=${limit}`);

// Admin
export const getScoringWeights = () => api.get('/admin/weights');
export const updateScoringWeights = (weights) => api.post('/admin/weights', null, { params: weights });
export const getUsers = () => api.get('/admin/users');

// Organization Management
export const getOrganizations = () => api.get('/organizations');
export const getOrganization = (orgId) => api.get(`/organizations/${orgId}`);
export const createOrganization = (data) => api.post('/organizations', data);
export const updateOrganization = (orgId, data) => api.put(`/organizations/${orgId}`, data);

// User Management
export const inviteUser = (data) => api.post('/admin/users/invite', data);
export const updateUserRole = (userId, role) => api.put(`/admin/users/${userId}/role`, { role });
export const deactivateUser = (userId) => api.put(`/admin/users/${userId}/deactivate`);

// Data Retention
export const deleteUpload = (uploadId) => api.delete(`/admin/uploads/${uploadId}`);

// Alerts
export const getAlerts = () => api.get('/alerts');
export const updateReviewStatus = (genericId, status) => api.post(`/alerts/${genericId}/review?status=${status}`);

// Therapeutic Areas
export const getTherapeuticAreas = () => api.get('/therapeutic-areas');

// Benchmark (no auth required)
export const getBenchmark = (region, hospitalType, size) => api.post('/benchmark', { region, hospital_type: hospitalType, size });

// Daily Loss
export const getDailyLoss = () => api.get('/daily-loss');

// Audit Logs
export const getAuditLogs = (limit = 100) => api.get(`/audit-logs?limit=${limit}`);

// Regulatory Sources
export const getRegulatorySources = (region) => api.get('/regulatory-sources' + (region ? `?region=${region}` : ''));

// Source Health
export const getSourceHealth = () => api.get('/source-health');

// Peer Intelligence
export const getPeerIntelligence = () => api.get('/peer-intelligence');

// Upload & Analysis
export const uploadFile = (formData) => api.post('/upload/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getUploadAnalysis = (uploadId) => api.get(`/upload/${uploadId}/analysis`);
export const getUploadInsights = (uploadId) => api.get(`/upload/${uploadId}/insights`);
export const getUploads = () => api.get('/uploads');
export const downloadSampleTemplate = () => api.get('/upload/sample-template', { responseType: 'blob' });

export default api;
